import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Sparkles, Mail, Lock, Eye, EyeOff, User, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const Register = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { signInWithGoogle, user, loading: authLoading } = useAuth();

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    const { error } = await signInWithGoogle();

    if (error) {
      toast.error(t("เกิดข้อผิดพลาดในการสมัครด้วย Google", "Error signing up with Google"));
      setIsGoogleLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-[120px]" />
      </div>

      <Card variant="glass" className="w-full max-w-md relative z-10 animate-scale-in">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold text-gradient">GenCNX</span>
          </Link>
          <CardTitle className="text-2xl">{t("สร้างบัญชี", "Create Account")}</CardTitle>
          <CardDescription>{t("เริ่มสร้างผลงาน AI สุดเจ๋งวันนี้", "Start creating amazing AI content today")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/10">
              <p className="text-sm text-foreground/80">
                {t(
                  "เพื่อความปลอดภัยและความสะดวก ระบบรองรับการสมัครสมาชิกผ่าน Google เท่านั้นในขณะนี้",
                  "For security and convenience, registration is currently limited to Google sign-up only."
                )}
              </p>
            </div>

            <Button
              type="button"
              variant="glow"
              className="w-full gap-2 py-6 text-lg"
              disabled={isGoogleLoading}
              onClick={handleGoogleSignIn}
            >
              {isGoogleLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              {t("สมัครด้วย Google", "Sign up with Google")}
            </Button>

            <div className="text-xs text-muted-foreground text-center">
              {t("โดยการสมัครสมาชิก คุณยอมรับ", "By signing up, you agree to our")}{" "}
              <Link to="/terms" className="text-primary hover:underline">{t("ข้อกำหนดการใช้บริการ", "Terms of Service")}</Link>
              {" "}{t("และ", "and")}{" "}
              <Link to="/privacy" className="text-primary hover:underline">{t("นโยบายความเป็นส่วนตัว", "Privacy Policy")}</Link>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            {t("มีบัญชีอยู่แล้ว?", "Already have an account?")}{" "}
            <Link to="/login" className="text-primary hover:underline font-medium">
              {t("เข้าสู่ระบบ", "Sign in")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;